import { useEffect, useState } from "react";
import { TokuroLogo } from "@/components/brand/TokuroLogo";

const SESSION_KEY = "tokuro:splash-seen";
const HOLD_MS = 1400;
const EXIT_MS = 420;

/**
 * Branded first-load splash. Renders once per browser session (sessionStorage-gated),
 * plays a short reveal animation, then fades out and unmounts.
 *
 * Timers are attached to `window` (not to a React ref) so React 18 StrictMode's
 * double-invocation and any concurrent-render pauses can't drop the "hide" trigger.
 * Once scheduled on the first mount, the timer runs to completion regardless of
 * remounts, and updates a single global state via a CustomEvent that any surviving
 * LaunchSplash instance listens for.
 */

const HIDE_EVENT = "tokuro:splash-hide";
const FADE_EVENT = "tokuro:splash-fade";

function scheduleHideOnce() {
  if (typeof window === "undefined") return;
  if (window.__tokuroSplashScheduled) return;
  window.__tokuroSplashScheduled = true;
  try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* ignore */ }
  window.setTimeout(() => {
    window.dispatchEvent(new CustomEvent(FADE_EVENT));
    window.setTimeout(() => window.dispatchEvent(new CustomEvent(HIDE_EVENT)), EXIT_MS);
  }, HOLD_MS);
}

export function LaunchSplash() {
  const [visible, setVisible] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      if (sessionStorage.getItem(SESSION_KEY) === "1") return false;
    } catch { /* ignore */ }
    return true;
  });
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (!visible) return undefined;
    scheduleHideOnce();
    const onFade = () => setFading(true);
    const onHide = () => setVisible(false);
    window.addEventListener(FADE_EVENT, onFade);
    window.addEventListener(HIDE_EVENT, onHide);
    return () => {
      window.removeEventListener(FADE_EVENT, onFade);
      window.removeEventListener(HIDE_EVENT, onHide);
    };
  }, [visible]);

  if (!visible) return null;

  const dismiss = () => {
    setFading(true);
    window.setTimeout(() => setVisible(false), EXIT_MS);
  };

  return (
    <div
      role="presentation"
      onClick={dismiss}
      data-testid="launch-splash"
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#0A1230]"
      style={{
        opacity: fading ? 0 : 1,
        transition: `opacity ${EXIT_MS}ms ease-out`,
        pointerEvents: fading ? "none" : "auto",
      }}
    >
      {/* Warm accent glow — matches the sign-in aesthetic */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-32 -right-24 h-[640px] w-[640px] rounded-full opacity-40 blur-3xl"
        style={{ background: "radial-gradient(circle, #C9762A 0%, transparent 62%)" }}
      />

      <div className="relative z-10 splash-reveal">
        <TokuroLogo variant="light" size="xl" showTagline testId="splash-logo" />
      </div>

      <style>{`
        @keyframes splashMarkReveal {
          0%   { transform: translateY(14px) scale(0.86); opacity: 0; filter: blur(6px); }
          60%  { transform: translateY(-2px) scale(1.02); opacity: 1; filter: blur(0); }
          100% { transform: translateY(0) scale(1);       opacity: 1; filter: blur(0); }
        }
        .splash-reveal { animation: splashMarkReveal 900ms cubic-bezier(.2,.7,.2,1) both; }
      `}</style>
    </div>
  );
}

export default LaunchSplash;
